'use client';

import { useEffect, useState } from 'react';
import { useI18n } from '@/lib/i18n/context';
import { LOCALES } from '@/locales';
import type { Locale } from '@/locales';
import { frontPageStrings } from '@/locales/frontpage';

const STORAGE_KEY = 'hummini_locale';

// Hộp nhỏ góc phải trên, hiện lần đầu truy cập, không chặn nội dung, có thể tắt.
// Mặc định chọn sẵn ngôn ngữ hệ thống (I18nProvider đã detect).
// Chỉ đổi ngôn ngữ giao diện, không lọc nội dung.
export function LanguagePrompt() {
  const { locale, setLocale } = useI18n();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setOpen(true);
    } catch { /* storage unavailable: không hiện hộp */ }
  }, []);

  if (!open) return null;

  const s = frontPageStrings[locale];

  // Tắt hộp cũng ghi nhận ngôn ngữ hiện tại để không hỏi lại.
  const close = () => {
    setLocale(locale);
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-labelledby="lang-prompt-title"
      className="fixed right-3 top-[72px] z-[60] w-[min(20rem,calc(100vw-1.5rem))] rounded-2xl border border-surface-700 bg-surface-900 p-4 shadow-2xl"
    >
      <div className="flex items-start justify-between gap-2">
        <h2 id="lang-prompt-title" className="font-display text-base font-bold text-white">{s.langTitle}</h2>
        <button
          type="button"
          onClick={close}
          aria-label={s.langClose}
          className="-mr-2 -mt-2 flex h-11 w-11 items-center justify-center rounded-xl text-text-secondary hover:bg-surface-800 hover:text-white"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <p className="mb-3 text-xs text-text-muted">{s.langHint}</p>

      <div role="radiogroup" aria-label={s.langTitle} className="grid max-h-[50vh] grid-cols-2 gap-1.5 overflow-y-auto">
        {LOCALES.map(({ code, label }) => (
          <button
            key={code}
            type="button"
            role="radio"
            aria-checked={code === locale}
            onClick={() => { setLocale(code as Locale); setOpen(false); }}
            className={`min-h-[44px] rounded-xl border px-2 text-sm font-medium transition-colors ${
              code === locale
                ? 'border-brand-500 bg-brand-700 text-white'
                : 'border-surface-700 text-text-secondary hover:bg-surface-800 hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
